// Lõi gọi Microsoft Graph từ trình duyệt bằng token delegated của người đăng nhập (SPA + PKCE, không OBO).
// Gói này KHÔNG biết MSAL: Hub truyền vào `token(scopes)`. Không log nội dung thư / lịch.

/** Nhóm scope theo nhu cầu — xin dần khi mở Box, không xin hết lúc đăng nhập. */
export const SCOPES = {
  mailRead: ['Mail.Read', 'Mail.Read.Shared'],
  mailWrite: ['Mail.ReadWrite', 'Mail.ReadWrite.Shared'],
  mailSend: ['Mail.Send', 'Mail.Send.Shared'],
  calendar: ['Calendars.ReadWrite', 'Calendars.ReadWrite.Shared'],
  mailboxSettings: ['MailboxSettings.Read'],
  people: ['People.Read', 'User.ReadBasic.All'],
} as const;
export type ScopeGroup = keyof typeof SCOPES;

/** `noperm` = 401/403 hoặc thiếu consent → màn ra trạng thái `noperm`; `throttled` = 429; `other` = còn lại. */
export type GraphErrorKind = 'noperm' | 'throttled' | 'notfound' | 'other';

export class GraphError extends Error {
  constructor(
    readonly status: number,
    readonly kind: GraphErrorKind,
    readonly code?: string,
  ) {
    super(`graph ${status}${code ? ` ${code}` : ''}`); // không đưa nội dung phản hồi vào message
  }
}

export const kindOfStatus = (s: number): GraphErrorKind => (s === 401 || s === 403 ? 'noperm' : s === 429 ? 'throttled' : s === 404 ? 'notfound' : 'other');

/** Lỗi thiếu consent / cần tương tác từ MSAL cũng quy về `noperm`. */
export function isNoPerm(e: unknown): boolean {
  if (e instanceof GraphError) return e.kind === 'noperm';
  const code = (e as { errorCode?: string } | null)?.errorCode ?? '';
  return /consent_required|interaction_required|login_required|insufficient_scope/i.test(code);
}

export interface GraphOptions {
  /** Lấy access token cho các scope Graph (Hub: acquireTokenSilent, thiếu thì popup). */
  token: (scopes: string[]) => Promise<string>;
  fetch?: typeof fetch;
  base?: string;
}

export interface Page<T> {
  items: T[];
  /** URL trang kế (đưa lại cho `next` của hàm list) */
  next?: string;
}

export interface Http {
  json<T>(group: ScopeGroup | ScopeGroup[], method: string, path: string, body?: unknown, headers?: Record<string, string>): Promise<T>;
  /** path tuyệt đối hoặc tương đối với base — dùng cho @odata.nextLink */
  absolute(path: string): string;
}

const GRAPH = 'https://graph.microsoft.com/v1.0';

export function createHttp(o: GraphOptions): Http {
  const f = o.fetch ?? fetch;
  const base = o.base ?? GRAPH;
  const absolute = (p: string) => (p.startsWith('https://') ? assertGraph(p, base) : base + p);
  return {
    absolute,
    async json<T>(group: ScopeGroup | ScopeGroup[], method: string, path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
      const scopes = [...new Set((Array.isArray(group) ? group : [group]).flatMap((g) => SCOPES[g]))];
      let token: string;
      try {
        token = await o.token(scopes);
      } catch (e) {
        if (isNoPerm(e)) throw new GraphError(403, 'noperm', 'consent');
        throw e;
      }
      const r = await f(absolute(path), {
        method,
        headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      if (!r.ok) {
        let code: string | undefined;
        try {
          code = ((await r.json()) as { error?: { code?: string } }).error?.code;
        } catch {
          /* không đọc được thân lỗi — bỏ qua */
        }
        throw new GraphError(r.status, kindOfStatus(r.status), code);
      }
      if (r.status === 202 || r.status === 204) return undefined as T;
      return (await r.json()) as T;
    },
  };
}

/** @odata.nextLink chỉ được trỏ về Graph — không gửi token tới nơi khác. */
function assertGraph(url: string, base: string): string {
  if (!url.startsWith(base) && !url.startsWith(GRAPH)) throw new GraphError(400, 'other', 'bad_next_link');
  return url;
}

export const enc = encodeURIComponent;

/** Hộp thư: `me` (mặc định) hoặc hộp thư chung `/users/{địa chỉ}`. */
export const box = (mailbox?: string) => (mailbox ? `/users/${enc(mailbox)}` : '/me');

export function toPage<T>(r: { value: T[]; '@odata.nextLink'?: string }): Page<T> {
  return { items: r.value, next: r['@odata.nextLink'] };
}

/** URLSearchParams → chuỗi truy vấn với %20 (không dùng '+') cho Graph. */
export const qs = (q: URLSearchParams) => q.toString().replace(/\+/g, '%20');
